import { useTranslation } from 'react-i18next';
import { Calendar, Users, Clock, Plane, PartyPopper, Home } from 'lucide-react';
import { useEffect, useState } from 'react';
import { TIMING } from '@/lib/utils';
import { daysFromToday, formatGuestDate, formatGuestTime, nightsBetween } from './guestDates';

interface WelcomeCardProps {
  guestName: string;
  unitName: string;
  propertyName: string;
  checkIn: string;
  checkOut: string;
  checkOutTime?: string | null;
  /** Check-in time ("15:00") — shown under the arrival date when set. */
  checkInTime?: string | null;
  guestsCount: number;
  adultsCount?: number;
  childrenCount?: number;
  infantsCount?: number;
  coverImage?: string | null;
  logoUrl?: string | null;
  darkMode: boolean;
}

export function WelcomeCard({
  guestName,
  unitName,
  propertyName,
  checkIn,
  checkOut,
  checkOutTime,
  checkInTime,
  guestsCount,
  adultsCount,
  childrenCount,
  // infantsCount,
  coverImage,
  logoUrl,
}: WelcomeCardProps) {
  const { t, i18n } = useTranslation();

  // Booking dates are calendar days ("YYYY-MM-DD") — parse them as LOCAL days
  // (new Date('YYYY-MM-DD') is UTC and showed the previous day in the Americas).
  const formatDate = (dateStr: string) => formatGuestDate(dateStr, i18n.language, { day: 'numeric', month: 'short' });
  const nights = nightsBetween(checkIn, checkOut);
  const checkInTimeLabel = formatGuestTime(checkInTime);
  const checkOutTimeLabel = formatGuestTime(checkOutTime);

  const firstName = (guestName || '').trim().split(' ')[0];

  // Animation state
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger animation after mount
    const timer = setTimeout(() => setIsVisible(true), TIMING.ANIMATION_MOUNT);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="relative overflow-hidden rounded-none md:rounded-[2rem] shadow-2xl group min-h-[540px] md:min-h-[600px]">
      {/* Background Image with Enhanced Overlay */}
      <div className="absolute inset-0">
        {coverImage ? (
          <img
            src={coverImage}
            alt={unitName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-cyan-500 via-blue-600 to-blue-700">
            {/* Decorative fallback illustration */}
            <div className="absolute inset-0 flex items-center justify-center opacity-10">
              <Home className="w-48 h-48 text-white" />
            </div>
          </div>
        )}
        {/* Multi-layer overlay - Tamniji za bolju vidljivost slike */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/10 via-transparent to-cyan-900/10" />
      </div>

      {/* Static Sparkle Effects - no animations for mobile performance */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-8 left-8 w-2 h-2 bg-white/40 rounded-full" />
        <div className="absolute top-16 right-12 w-1.5 h-1.5 bg-yellow-300/50 rounded-full" />
        <div className="absolute bottom-40 left-16 w-1 h-1 bg-white/30 rounded-full" />
        <div className="absolute top-28 right-6 w-2.5 h-2.5 bg-blue-400/40 rounded-full" />
        <div className="absolute top-12 left-1/3 w-1 h-1 bg-cyan-300/40 rounded-full" />
      </div>

      {/* Logo in top right corner - no background, just the logo */}
      {logoUrl && (
        <div className="absolute top-4 right-4 z-10">
          <img
            src={logoUrl}
            alt={propertyName}
            className="h-12 w-auto object-contain drop-shadow-lg"
          />
        </div>
      )}

      {/* Content */}
      <div className="relative p-5 pt-12 pb-5 h-full flex flex-col">
        {/* Top section - just countdown badge wrapper */}
        <div className="mb-3">
        {/* Countdown Badge - Shows days until arrival or current stay status */}
        {(() => {
          const arrivalDays = daysFromToday(checkIn);
          // No (valid) check-in date: no badge — it used to fall back to 0 and
          // claim "Arriving today!".
          if (arrivalDays === null) return null;
          const daysUntilArrival = arrivalDays;
          const daysUntilDeparture = daysFromToday(checkOut) ?? daysUntilArrival;

          // Already checked out
          if (daysUntilDeparture < 0) {
            return (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-slate-500/20 to-gray-500/20 bg-black/20 border border-gray-400/30 shadow-lg">
                <Home className="w-3.5 h-3.5 text-gray-300" />
                <span className="text-xs font-semibold text-gray-100 tracking-wide">
                  {t('guest.stayCompleted', 'Boravak završen')}
                </span>
              </div>
            );
          }

          // Arriving today
          if (daysUntilArrival === 0) {
            return (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-500/20 to-yellow-500/20 bg-black/20 border border-yellow-400/30 shadow-lg">
                <Plane className="w-3.5 h-3.5 text-yellow-300" />
                <span className="text-xs font-semibold text-yellow-100 tracking-wide">
                  {t('guest.arrivingToday', 'Dolazak danas!')}
                </span>
              </div>
            );
          }

          // Currently staying
          if (daysUntilArrival < 0 && daysUntilDeparture >= 0) {
            return (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-emerald-500/20 to-green-500/20 bg-black/20 border border-emerald-400/30 shadow-lg">
                <PartyPopper className="w-3.5 h-3.5 text-emerald-300" />
                <span className="text-xs font-semibold text-emerald-100 tracking-wide">
                  {t('guest.enjoyYourStay', 'Uživajte u boravku!')}
                </span>
              </div>
            );
          }

          // Arriving tomorrow
          if (daysUntilArrival === 1) {
            return (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-orange-500/20 to-amber-500/20 bg-black/20 border border-orange-400/30 shadow-lg">
                <Plane className="w-3.5 h-3.5 text-orange-300" />
                <span className="text-xs font-semibold text-orange-100 tracking-wide">
                  {t('guest.arrivingTomorrow', 'Dolazak sutra!')}
                </span>
              </div>
            );
          }

          // Countdown days
          return (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-blue-500/20 to-cyan-500/20 bg-black/20 border border-blue-400/30 shadow-lg">
              <Plane className="w-3.5 h-3.5 text-blue-300" />
              <div className="flex items-baseline gap-1">
                <span className="text-lg font-black text-white tabular-nums">{daysUntilArrival}</span>
                <span className="text-xs font-semibold text-blue-100 tracking-wide">
                  {/* Always 2+ here (0 and 1 have their own badges); plural forms
                      give hr "21 dan" / "22 dana". Falls back to the old label
                      until guest.daysUntilArrivalCount exists. */}
                  {t('guest.daysUntilArrivalCount', t('guest.daysUntilArrival', 'dana'), { count: daysUntilArrival })}
                </span>
              </div>
            </div>
          );
        })()}
        </div>{/* End countdown wrapper */}

        {/* Welcome Message - Veći i dominantniji */}
        <div className="space-y-2 mb-24">
          {/* Greeting - VEĆI s glow animacijom */}
          <div className="relative overflow-hidden mb-8">
            <h2
              className={`text-3xl sm:text-4xl font-black text-white tracking-tight leading-tight transition-all duration-700 ease-out ${
                isVisible
                  ? 'opacity-100 translate-y-0 animate-pulse-glow'
                  : 'opacity-0 translate-y-8'
              }`}
              style={{
                textShadow: '0 0 30px rgba(255, 255, 255, 0.5), 0 4px 12px rgba(0, 0, 0, 0.6), 0 0 60px rgba(59, 130, 246, 0.3)',
              }}
            >
              {/* No "Welcome, !" when the booking has no guest name. */}
              {firstName ? `${t('guest.hello', 'Dobrodošli')}, ${firstName}!` : `${t('guest.hello', 'Dobrodošli')}!`}
            </h2>
          </div>

          {/* Property name - VEĆI s gradient animacijom */}
          <div
            className={`flex items-center justify-center gap-3 transition-all duration-700 ease-out delay-200 ${
              isVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-6'
            }`}
          >
            <div
              className="w-8 h-1 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full"
              style={{
                boxShadow: '0 0 15px rgba(6, 182, 212, 0.5)'
              }}
            />
            <h3
              className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text leading-tight animate-gradient truncate max-w-[calc(100vw-6rem)]"
              style={{
                backgroundImage: 'linear-gradient(90deg, #dbeafe, #a5f3fc, #5eead4, #a5f3fc, #dbeafe)',
                backgroundSize: '200% 100%',
                animation: isVisible ? 'gradient-shift 6s ease-in-out infinite' : 'none',
              }}
            >
              {propertyName}
            </h3>
            <div
              className="w-8 h-1 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full"
              style={{
                boxShadow: '0 0 15px rgba(6, 182, 212, 0.5)'
              }}
            />
          </div>

          {/* Unit name - prominentniji s shimmer efektom. Skipped when it's the
              same as the property name (a whole villa rented as one unit). */}
          {unitName.trim().toLowerCase() !== propertyName.trim().toLowerCase() && (
          <div
            className={`flex items-center justify-center gap-3 mt-3 transition-all duration-700 ease-out delay-400 ${
              isVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-4'
            }`}
          >
            <div
              className="w-8 h-1 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full"
              style={{
                boxShadow: '0 0 15px rgba(6, 182, 212, 0.5)'
              }}
            />
            <p
              className="text-white text-xl sm:text-2xl font-black tracking-wide truncate max-w-[calc(100vw-6rem)]"
              style={{
                textShadow: '0 0 25px rgba(255, 255, 255, 0.5), 0 2px 10px rgba(0, 0, 0, 0.5), 0 0 50px rgba(6, 182, 212, 0.4)',
              }}
            >
              {unitName}
            </p>
            <div
              className="w-8 h-1 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full"
              style={{
                boxShadow: '0 0 15px rgba(6, 182, 212, 0.5)'
              }}
            />
          </div>
          )}
        </div>

        {/* CSS Animations - reduced for mobile performance */}
        <style>{`
          @keyframes gradient-shift {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
        `}</style>

        {/* Cards Section - kompaktnije, pushed to bottom */}
        <div className="mt-auto space-y-3">
          <div className="grid grid-cols-3 gap-3">
            {/* Dolazak */}
            <div className="group bg-white/10 border border-white/20 rounded-xl p-3 text-center cursor-default shadow-lg">
              <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shadow-xl shadow-blue-500/30">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <p className="text-[9px] text-white/50 uppercase tracking-widest font-bold leading-tight mb-1">
                {t('guest.checkIn', 'Dolazak')}
              </p>
              <p className="font-black text-white text-sm tracking-tight">
                {formatDate(checkIn)}
              </p>
              {checkInTimeLabel && (
                <p className="text-[10px] text-white/70 mt-0.5 font-semibold tabular-nums">
                  {t('guest.houseRules.from', 'od')} {checkInTimeLabel}
                </p>
              )}
            </div>

            {/* Noćenja */}
            <div className="group bg-white/10 border border-white/20 rounded-xl p-3 text-center cursor-default shadow-lg">
              <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center shadow-xl shadow-cyan-500/30">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <p className="text-[9px] text-white/50 uppercase tracking-widest font-bold leading-tight mb-1">
                {t('guest.nights', 'Noćenja')}
              </p>
              <p className="font-black text-white text-2xl tabular-nums">{nights}</p>
            </div>

            {/* Gosti */}
            <div className="group bg-white/10 border border-white/20 rounded-xl p-3 text-center cursor-default shadow-lg">
              <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-xl shadow-emerald-500/30">
                <Users className="w-5 h-5 text-white" />
              </div>
              <p className="text-[9px] text-white/50 uppercase tracking-widest font-bold leading-tight mb-1">
                {t('guest.guests', 'Gosti')}
              </p>
              {/* Show detailed breakdown if available */}
              {/* Breakdown only when there are kids — language-neutral symbols
                  (was a hard-coded Croatian "2o · 1d" every guest saw). */}
              {adultsCount !== undefined && adultsCount > 0 && childrenCount !== undefined && childrenCount > 0 ? (
                <div>
                  <p className="font-black text-white text-2xl tabular-nums">{guestsCount}</p>
                  <p className="text-[10px] text-white/60 mt-0.5 leading-tight font-medium" aria-label={`${adultsCount} + ${childrenCount}`}>
                    {adultsCount} 🧑 · {childrenCount} 🧒
                  </p>
                </div>
              ) : (
                <p className="font-black text-white text-2xl tabular-nums">{guestsCount}</p>
              )}
            </div>
          </div>

          {/* Check-out Banner - moderan dizajn */}
          <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-400/30 rounded-xl p-3 flex items-center justify-between shadow-lg">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
                <span className="text-base">🏠</span>
              </div>
              <div>
                <p className="text-[9px] text-amber-200/70 uppercase tracking-widest font-bold leading-tight mb-0.5">
                  {t('guest.checkOut', 'Odlazak')}
                </p>
                <p className="font-black text-white text-sm tracking-tight">
                  {formatDate(checkOut)}
                </p>
              </div>
            </div>
            {/* Only a time the host actually set — never an invented default. */}
            {checkOutTimeLabel && (
              <div className="text-right">
                <p className="text-[9px] text-amber-200/60 leading-tight mb-0.5 font-semibold">
                  {t('guest.until', 'do')}
                </p>
                <p className="font-black text-amber-300 text-xl tabular-nums">
                  {checkOutTimeLabel}
                </p>
              </div>
            )}
          </div>
        </div>{/* End of mt-auto cards section */}
      </div>
    </div>
  );
}

export default WelcomeCard;
